import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DistributionCenter } from '../types/DistributionCenter';

export interface DistributionCentersState {
  core: {
    centers: DistributionCenter[];
    loading: boolean;
    loaded: boolean;
    error: string | null;
  };
  byElection: {
    [electionId: string]: {
      centers: DistributionCenter[];
      loading: boolean;
      loaded: boolean;
      error: string | null;
      importingById: Record<string, boolean>;
    };
  };
}

const initialElectionState = {
  centers: [],
  loading: false,
  loaded: false,
  error: null,
  importingById: {},
};

const initialState: DistributionCentersState = {
  core: { ...initialElectionState },
  byElection: {},
};

const distributionCentersSlice = createSlice({
  name: 'distributionCenters',
  initialState,
  reducers: {
    fetchDistributionCentersStart: (state, action: PayloadAction<{ electionId?: string }>) => {
      if (action.payload.electionId) {
        if (!state.byElection[action.payload.electionId]) {
          state.byElection[action.payload.electionId] = { ...initialElectionState };
        }
        state.byElection[action.payload.electionId].loading = true;
        state.byElection[action.payload.electionId].loaded = false;
        state.byElection[action.payload.electionId].error = null;
      } else {
        state.core.loading = true;
        state.core.loaded = false;
        state.core.error = null;
      }
    },
    fetchDistributionCentersSuccess: (state, action: PayloadAction<{ centers: DistributionCenter[]; electionId?: string }>) => {
      const { centers, electionId } = action.payload;
      if (electionId) {
        if (!state.byElection[electionId]) {
          state.byElection[electionId] = { ...initialElectionState };
        }
        state.byElection[electionId].centers = centers;
        state.byElection[electionId].loading = false;
        state.byElection[electionId].loaded = true;
      } else {
        state.core.centers = centers;
        state.core.loading = false;
        state.core.loaded = true;
      }
    },
    fetchDistributionCentersFailure: (state, action: PayloadAction<{ error: string; electionId?: string }>) => {
      const { error, electionId } = action.payload;
      if (electionId) {
        if (!state.byElection[electionId]) {
          state.byElection[electionId] = { ...initialElectionState };
        }
        state.byElection[electionId].error = error;
        state.byElection[electionId].loading = false;
        state.byElection[electionId].loaded = true;
      } else {
        state.core.error = error;
        state.core.loading = false;
        state.core.loaded = true;
      }
    },
    addOrEditDistributionCenterStart: (state, action: PayloadAction<{ center: DistributionCenter; electionId?: string }>) => {
      if (action.payload.electionId) {
        if (!state.byElection[action.payload.electionId]) {
          state.byElection[action.payload.electionId] = { ...initialElectionState };
        }
        state.byElection[action.payload.electionId].loading = true;
        state.byElection[action.payload.electionId].error = null;
      } else {
        state.core.loading = true;
        state.core.error = null;
      }
    },
    addOrEditDistributionCenterSuccess: (state, action: PayloadAction<{ center: DistributionCenter; electionId?: string }>) => {
      const { center, electionId } = action.payload;
      const targetState = electionId ? state.byElection[electionId] : state.core;

      if (!targetState) return;

      const index = targetState.centers.findIndex((dc) => dc.id === center.id);
      if (index >= 0) {
        targetState.centers[index] = center;
      } else {
        targetState.centers.push(center);
      }
      targetState.loading = false;
    },
    addOrEditDistributionCenterFailure: (state, action: PayloadAction<{ error: string; electionId?: string }>) => {
      const { error, electionId } = action.payload;
      if (electionId) {
        if (!state.byElection[electionId]) {
          state.byElection[electionId] = { ...initialElectionState };
        }
        state.byElection[electionId].error = error;
        state.byElection[electionId].loading = false;
      } else {
        state.core.error = error;
        state.core.loading = false;
      }
    },
    deleteDistributionCenterStart: (state, action: PayloadAction<{ id: string; electionId?: string }>) => {
      if (action.payload.electionId) {
        if (!state.byElection[action.payload.electionId]) {
          state.byElection[action.payload.electionId] = { ...initialElectionState };
        }
        state.byElection[action.payload.electionId].loading = true;
        state.byElection[action.payload.electionId].error = null;
      } else {
        state.core.loading = true;
        state.core.error = null;
      }
    },
    deleteDistributionCenterSuccess: (state, action: PayloadAction<{ id: string; electionId?: string }>) => {
      const { id, electionId } = action.payload;
      if (electionId) {
        if (state.byElection[electionId]) {
          state.byElection[electionId].centers = state.byElection[electionId].centers.filter((dc) => dc.id !== id);
          state.byElection[electionId].loading = false;
        }
      } else {
        state.core.centers = state.core.centers.filter((dc) => dc.id !== id);
        state.core.loading = false;
      }
    },
    deleteDistributionCenterFailure: (state, action: PayloadAction<{ error: string; electionId?: string }>) => {
      const { error, electionId } = action.payload;
      if (electionId) {
        if (!state.byElection[electionId]) {
          state.byElection[electionId] = { ...initialElectionState };
        }
        state.byElection[electionId].error = error;
        state.byElection[electionId].loading = false;
      } else {
        state.core.error = error;
        state.core.loading = false;
      }
    },
    importDistributionCenterToElectionStart: (state, action: PayloadAction<{ electionId: string; distributionCenterId: string }>) => {
      if (!state.byElection[action.payload.electionId]) {
        state.byElection[action.payload.electionId] = { ...initialElectionState };
      }
      state.byElection[action.payload.electionId].importingById[action.payload.distributionCenterId] = true;
    },
    importDistributionCenterToElectionSuccess: (state, action: PayloadAction<{ electionId: string; center: DistributionCenter }>) => {
      const { electionId, center } = action.payload;
      if (!state.byElection[electionId]) {
        state.byElection[electionId] = { ...initialElectionState };
      }
      const index = state.byElection[electionId].centers.findIndex((dc) => dc.id === center.id);
      if (index >= 0) {
        state.byElection[electionId].centers[index] = center;
      } else {
        state.byElection[electionId].centers.push(center);
      }
      state.byElection[electionId].loading = false;
      delete state.byElection[electionId].importingById[center.id];
      state.byElection[action.payload.electionId].importingById[center.coreId ?? ''] = false;
    },
    importDistributionCenterToElectionFailure: (state, action: PayloadAction<{ electionId: string; error: string; distributionCenterId: string }>) => {
      const { electionId, distributionCenterId } = action.payload;
      if (!state.byElection[electionId]) {
        state.byElection[electionId] = { ...initialElectionState };
      }
      delete state.byElection[electionId].importingById[distributionCenterId];
    },
  },
});

export const {
  fetchDistributionCentersStart,
  fetchDistributionCentersSuccess,
  fetchDistributionCentersFailure,
  addOrEditDistributionCenterStart,
  addOrEditDistributionCenterSuccess,
  addOrEditDistributionCenterFailure,
  deleteDistributionCenterStart,
  deleteDistributionCenterSuccess,
  deleteDistributionCenterFailure,
  importDistributionCenterToElectionStart,
  importDistributionCenterToElectionSuccess,
  importDistributionCenterToElectionFailure,
} = distributionCentersSlice.actions;

export default distributionCentersSlice.reducer;
